import 'react-app-polyfill/ie9';
import 'react-app-polyfill/ie11';
import 'react-app-polyfill/stable';

import 'fastestsmallesttextencoderdecoder';

import * as React from 'react';

import * as Sentry from '@sentry/browser';
import * as Integrations from '@sentry/integrations';
import ReactDOM from 'react-dom';
import ReactGA from 'react-ga4';

import App from './app';
import { polyfillFinally } from './ie-11-finally-polyfill';

import { isProd } from './config/config';

import './index.scss';

polyfillFinally();

// Lifted mostly from sentry-testkit, but didn't want to bundle
// the whole thing just to prevent sending to Sentry locally
class MockTransport {
  // eslint-disable-next-line class-methods-use-this
  sendEvent(event: Sentry.Event): PromiseLike<any> {
    // eslint-disable-next-line no-console
    console.error('[mock] Sentry', event);
    return Promise.resolve({
      status: 'success',
      event,
    } as any);
  }

  // eslint-disable-next-line class-methods-use-this
  close(): PromiseLike<boolean> {
    return Promise.resolve(true);
  }
}

const ignoreErrors = [
  // These errors are pretty much just noise. A very high percentage of the time, it is just the user
  // refreshing the page while we have a request out (or pressing the "X"). Read more here:
  //   - https://forum.sentry.io/t/typeerror-failed-to-fetch-reported-over-and-overe/8447
  //   - https://stackoverflow.com/a/60860369
  'Failed to fetch',
  'NetworkError when attempting to fetch resource.',
  'Load failed',
  'Cancelled',

  // The ResizeObserver is similarly an error thrown by Chrome when some drawing limit is reached, and has
  // no impact on our user. Read more here:
  //   - https://stackoverflow.com/a/50387233
  'ResizeObserver loop limit exceeded',
  'ResizeObserver loop completed with undelivered notifications',

  // The "object not found" error is caused by the "Safe Links" feature of Microsoft - which is scanning our
  // page with a stripped-down version of chromium that doesn't support certain JS features. It throws a weird
  // error that isn't an error (like throw ''), which results in a cryptic error. You can read more here:
  //  - https://github.com/getsentry/sentry-javascript/issues/3440#issuecomment-865857552
  'Object Not Found Matching Id',

  // These Auth0 errors are unavoidable - 'invalid state' happens when a user has bookmarked a page with a stale
  // code, or has otherwise interrupted the login process. 'login required' happens when their browser session
  // becomes stale - we attempt to handle this in api.ts, but there are apparently ways it can be circumvented
  'Invalid state',
  'Login required',
];

const isError = (possibleError: any): possibleError is Error =>
  possibleError && possibleError.stack && possibleError.message;

Sentry.init({
  dsn: 'https://eae78477ce894e368430c68cc5e94560@sentry.io/282819',
  normalizeDepth: 10,
  integrations: [
    new Integrations.ExtraErrorData({
      depth: 10,
    }),
  ],
  beforeSend: (event: Sentry.Event, hint: Sentry.EventHint) => {
    const error = hint.originalException;
    if (error) {
      // This first checks if the error we got is on our ignore errors list
      const isOnIgnoreError = ignoreErrors.find((ie) =>
        // This secondary cast to String is technically unnecessary, but being extra defensive in this code
        // as it's handling the error
        isError(error) ? error.message.includes(ie) : String(error).includes(ie),
      );

      // Next, we want to actually send through errors that have `tags` on them as
      // this indicates that we were manually trying to capture an exception
      // A particularly important example: in our action-dispatcher-factory we capture
      // the `action` tag. If we "Failed to fetch" that request, we want to know as it's
      // specifically failing to make a request to our server, and might be indicative of
      // a broader issue
      const hasTags = Object.keys(event.tags ?? {}).length > 0;

      return isOnIgnoreError && !hasTags ? null : event;
    }
    return event;
  },
  transport: !isProd ? MockTransport : undefined,
});

ReactGA.initialize(isProd ? 'G-8H00ZNPP0S' : 'G-SS0L0J1851');

const root = document.getElementById('root');
if (!root) {
  throw Error('Could not find root element');
}

ReactDOM.render(<App />, root);
